import { Button, Form, Input } from "antd"
import type { FormInstance } from "antd"
import { ArrowLeft, ArrowRight, Loader, Mail } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import OSAlert from "~/components/os-alert"

interface AuthOtpVerificationProps<T extends { otp: string }> {
  form?: FormInstance<T>
  title: string
  email: string
  error?: string | null
  isPending: boolean
  showResendSuccess: boolean
  autoSubmit?: boolean
  otpClassName?: string
  resendOtp: () => Promise<void>
  resetForm: () => void
  onFinish: (values: T) => void
}

export default function OtpVerification<T extends { otp: string }>({
  form,
  title,
  email,
  error,
  isPending,
  showResendSuccess,
  autoSubmit = false,
  otpClassName,
  resendOtp,
  resetForm,
  onFinish
}: AuthOtpVerificationProps<T>) {
  const [internalForm] = Form.useForm<T>()
  const otpForm = form ?? internalForm

  return (
    <Form
      layout="vertical"
      autoComplete="off"
      spellCheck={false}
      className="login-form"
      onFinish={onFinish}
      form={otpForm}
    >
      <div
        data-testid="otp-verification-card"
        className="rounded-2xl border border-gray-100 bg-white/80 shadow-sm shadow-primary/5 px-6 py-7 text-center"
      >
        <div className="flex items-center justify-center gap-3 mb-1.5">
          <div className="size-10 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/10 flex items-center justify-center shrink-0">
            <Mail size={18} className="text-primary" />
          </div>
          <h1 className="text-xl font-bold text-dark leading-tight">{title}</h1>
        </div>
        <p className="text-[13px] text-gray-400 leading-relaxed mb-5">
          {"We sent a 6-digit code to "}
          <span data-testid="otp-email" className="text-dark font-medium">
            {email}
          </span>
          <br />
          <span className="text-gray-400 text-xs">Expires in 30 minutes</span>
        </p>

        <Form.Item
          label=""
          name="otp"
          className="login-otp-field [&_.ant-otp]:w-full [&_.ant-otp_.ant-otp-input-wrapper]:flex-1 mb-0 max-w-[320px] mx-auto"
          rules={[
            {
              required: true,
              message: "Please enter the verification code"
            }
          ]}
        >
          <Input.OTP
            data-testid="otp-input"
            length={6}
            size="large"
            className={otpClassName}
            onChange={
              autoSubmit
                ? (val) => {
                    if (val.length === 6) {
                      setTimeout(() => otpForm.submit(), 0)
                    }
                  }
                : undefined
            }
          />
        </Form.Item>

        {autoSubmit ? (
          <button
            data-testid="otp-hidden-submit"
            type="submit"
            className="hidden"
            aria-label="Submit"
          />
        ) : (
          <Button
            data-testid="otp-verify-button"
            type="primary"
            htmlType="submit"
            block
            size="large"
            disabled={isPending}
            icon={
              isPending ? (
                <Loader size={16} className="animate-spin" />
              ) : (
                <ArrowRight size={16} />
              )
            }
            iconPosition="end"
            className="!rounded-xl !h-11 !text-sm !font-semibold mt-4 max-w-[320px] mx-auto"
          >
            {isPending ? "Verifying..." : "Verify"}
          </Button>
        )}

        {error && (
          <OSAlert
            data-testid="otp-error-alert"
            type="error"
            message={error}
            className="mt-3"
          />
        )}

        {autoSubmit && isPending && (
          <div
            data-testid="otp-verifying-indicator"
            className="flex items-center justify-center gap-2 text-sm text-primary font-medium mt-4"
          >
            <Loader size={16} className="animate-spin" />
            Verifying…
          </div>
        )}

        <div className="border-t border-gray-100 mt-5 pt-4">
          {showResendSuccess && (
            <OSAlert
              data-testid="otp-resend-success-alert"
              type="success"
              className="font-medium text-13 !text-left mb-3"
              message="OTP has been resent successfully."
            />
          )}
          <div className="flex items-center justify-between">
            <button
              data-testid="otp-back-button"
              type="button"
              className="text-xs flex items-center gap-1.5 text-gray-400 hover:text-dark transition-colors"
              onClick={resetForm}
            >
              <ArrowLeft size={14} />
              Back
            </button>
            {!error?.toLowerCase().includes("max otp requests reached") && (
              <ResendOtp resendOtp={resendOtp} />
            )}
          </div>
        </div>
      </div>
    </Form>
  )
}

const TIMER = 60

function ResendOtp({ resendOtp }: { resendOtp: () => Promise<void> }) {
  const [loading, setLoading] = useState(false)
  const [countdown, setCountdown] = useState(() => TIMER)
  const endTimeRef = useRef<number>(Date.now() + TIMER * 1000)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const startTimer = useCallback(() => {
    endTimeRef.current = Date.now() + TIMER * 1000
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
    const tick = () => {
      const remaining = Math.max(
        0,
        Math.round((endTimeRef.current - Date.now()) / 1000)
      )
      setCountdown(remaining)
      if (remaining <= 0 && intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
    intervalRef.current = setInterval(tick, 1000)
    tick()
  }, [])

  useEffect(() => {
    startTimer()
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [startTimer])

  return (
    <Button
      data-testid="otp-resend-button"
      htmlType="button"
      type="text"
      loading={loading}
      className={`text-xs p-0 h-auto flex hover:underline gap-1 font-medium ${countdown > 0 ? "cursor-not-allowed pointer-events-none text-dark-2" : "text-primary"}`}
      onClick={async () => {
        setLoading(true)
        await resendOtp()
        startTimer()
        setLoading(false)
      }}
    >
      Resend OTP
      {countdown > 0 && (
        <span className="w-14 text-left">
          {countdown > 0 ? ` in (${countdown}s)` : ""}
        </span>
      )}
    </Button>
  )
}
